import {Context, createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState} from 'react';

import {useUserConfig} from '../../service/user/user-config';
import {useUserFavoriteSectionsMutation} from '../../service/user/user-favorite-sections';
import {useUserLanguageMutation} from '../../service/user/user-language';
import {fetchUserOwn} from '../../service/user/user-own';
import {UserType} from '../../service/user/user-type';
import {useUserRoles as useUserManagement} from '../../service/user-management/user-management-user';
import {getCookie} from '../../util/cookie';
import {catchError} from '../../util/promise';
import {useHelpLink} from '../help-links/help-links-hook';
import {localeNameToShortLocaleNameMap, shortLocaleNameToLocaleNameMap} from '../locale/locale-context-const';
import {getIsAuthLanguageFromSessionStorage, getLanguageFromUrl} from '../locale/locale-context-helper';
import {LocaleNameEnum, ShortLocaleNameEnum} from '../locale/locale-context-type';
import {useLocale} from '../locale/locale-hook';

import {
    defaultUserContextData,
    DEMO_CABINET_URL_KEY,
    lpsFeatureUserIds,
    REPUTATION_MANAGER_ID,
    technicalSupportTestUserIds,
} from './user-context-helper';
import {UserContextType} from './user-context-type';
import {useUsetiful} from './usetiful-hook';

export const UserContext: Context<UserContextType> = createContext<UserContextType>(defaultUserContextData);

type PropsType = PropsWithChildren<Record<string, unknown>>;

// eslint-disable-next-line max-statements
export function UserProvider(props: PropsType): JSX.Element {
    const {children} = props;

    const [isInGettingUser, setIsInGettingUser] = useState<boolean>(true); // true by default to show spinner

    const [gettingUserError, setGettingUserError] = useState<Error | null>(null);
    const [user, setUser] = useState<UserType | null>(null);
    const [selectAfterFetching, setSelectAfterFetching] = useState<ShortLocaleNameEnum | null>(null);
    const [favorites, setFavorites] = useState<Array<string>>([]);

    const {mutate: updateUserLanguage} = useUserLanguageMutation();
    const {mutateAsync: updateFavoriteSections} = useUserFavoriteSectionsMutation();
    const userConfig = useUserConfig();
    const {getUserConfig} = userConfig;
    const {isFetchingLocaleData, setLocaleName, shortLocaleName} = useLocale();

    const demoCabinetHelpLink = useHelpLink(DEMO_CABINET_URL_KEY);
    const {data: userManagement, refetch} = useUserManagement();

    const {setRole} = useUsetiful();

    const isLanguageFromUrl = getLanguageFromUrl();
    const isLanguageSessionStorage = getIsAuthLanguageFromSessionStorage();
    const isReputationManager = userManagement?.role?.pk === REPUTATION_MANAGER_ID;
    const isTechnicalSupportUser = Boolean(user && technicalSupportTestUserIds.has(user.id));
    const isAllowedToSeeLps = Boolean(user?.id && lpsFeatureUserIds.has(user.id));

    const memoizedGettingUser = useCallback(
        function tryToGetUser(): Promise<UserType | Error> {
            setIsInGettingUser(true);
            setGettingUserError(null);
            getUserConfig();

            return fetchUserOwn()
                .then((result: UserType | Error): UserType | Error => {
                    if (result instanceof Error) {
                        setGettingUserError(result);
                    } else {
                        setUser(result);
                        setFavorites(result.favoriteSections);
                        setRole(result);
                        refetch();

                        // Usually we prefer to use backend language for consistency between browsers/devices, but
                        // if user changes language on login page (or has 'lang' query param) then it has more priority
                        if (result.language && !isLanguageFromUrl && !isLanguageSessionStorage) {
                            setSelectAfterFetching(result.language);
                        }
                    }

                    setIsInGettingUser(false);

                    // console.log('---> get user', result);
                    // console.log(JSON.stringify(result, null, 4));

                    return result;
                })
                .catch(catchError);
        },
        [getUserConfig, isLanguageSessionStorage, isLanguageFromUrl, refetch, setRole]
    );

    const refetchUser = useCallback((): Promise<UserType | Error> => {
        return fetchUserOwn()
            .then((result: UserType | Error): UserType | Error => {
                if (result instanceof Error) {
                    setGettingUserError(result);
                } else {
                    setUser(result);
                    setFavorites(result.favoriteSections);
                    refetch();
                }

                return result;
            })
            .catch(catchError);
    }, [refetch]);

    const resetUser = useCallback(() => {
        setUser(null);
        setFavorites([]);
        setIsInGettingUser(false);
    }, []);

    const persistFavorites = useCallback(
        async (nextFavorites: Array<string>) => {
            await updateFavoriteSections(nextFavorites);
            setFavorites(nextFavorites);
            setUser((previousUser) =>
                previousUser
                    ? {
                          ...previousUser,
                          favoriteSections: nextFavorites,
                      }
                    : previousUser
            );
        },
        [updateFavoriteSections]
    );

    const addFavorite = useCallback(
        async (path: string, childPaths: Array<string>) => {
            const childSet = new Set(childPaths);
            const withoutNested = favorites?.filter((favoritePath) => !childSet.has(favoritePath));

            if (withoutNested.includes(path)) {
                if (withoutNested?.length === favorites?.length) {
                    return;
                }

                await persistFavorites(withoutNested);
                return;
            }

            await persistFavorites([...withoutNested, path]);
        },
        [favorites, persistFavorites]
    );

    const removeFavorite = useCallback(
        async (path: string, childPaths: Array<string>) => {
            const toRemove = new Set([path, ...childPaths]);
            const next = favorites.filter((favoritePath) => !toRemove.has(favoritePath));

            if (next?.length === favorites?.length) {
                return;
            }

            await persistFavorites(next);
        },
        [favorites, persistFavorites]
    );

    const isFavorite = useCallback((path: string) => favorites?.includes(path), [favorites]);

    const setLocaleNameByUser = useCallback(
        (locale: LocaleNameEnum) => {
            if (user?.isDemoCpUser && demoCabinetHelpLink?.documentationLink) {
                window.location.assign(
                    `${demoCabinetHelpLink?.documentationLink}/${localeNameToShortLocaleNameMap[locale]}`
                );
            } else {
                setLocaleName(locale);
            }
        },
        [user?.isDemoCpUser, demoCabinetHelpLink, setLocaleName]
    );

    useEffect(() => {
        const token = getCookie('csrftoken');
        const sessionId = getCookie('sessionid');

        // if we have token and session id - user is authorized and so we do a request, otherwise we end loading
        if (token && sessionId) {
            memoizedGettingUser();
        } else {
            setIsInGettingUser(false);
        }
    }, [memoizedGettingUser]);

    useEffect(() => {
        if (!isFetchingLocaleData && selectAfterFetching) {
            if (shortLocaleName !== selectAfterFetching) {
                setLocaleName(shortLocaleNameToLocaleNameMap[selectAfterFetching]);
            }

            setSelectAfterFetching(null);
        }
    }, [isFetchingLocaleData, selectAfterFetching, setLocaleName, shortLocaleName]);

    useEffect(() => {
        if (user && user.language !== shortLocaleName) {
            updateUserLanguage(shortLocaleName);
            setUser({...user, language: shortLocaleName});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shortLocaleName, user]);

    const providerData: UserContextType = useMemo<UserContextType>((): UserContextType => {
        return {
            ...userConfig,
            getUser: memoizedGettingUser,
            refetchUser,
            resetUser,
            setLocaleNameByUser,
            user,
            isInGettingUser,
            gettingUserError,
            isReputationManager,
            isTechnicalSupportUser,
            isAllowedToSeeLps,
            favorites,
            addFavorite,
            removeFavorite,
            isFavorite,
        };
    }, [
        memoizedGettingUser,
        userConfig,
        refetchUser,
        resetUser,
        setLocaleNameByUser,
        user,
        isInGettingUser,
        gettingUserError,
        isReputationManager,
        isTechnicalSupportUser,
        isAllowedToSeeLps,
        favorites,
        addFavorite,
        removeFavorite,
        isFavorite,
    ]);

    return <UserContext.Provider value={providerData}>{children}</UserContext.Provider>;
}
